// src/components/travel/BNPLProviderSelection.tsx
import * as React from 'react';
import { createBnplCheckoutSession, BnplProvider } from '@/lib/checkout';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

type BnplProviderSelectionProps = {
  orderId: string;
  amount: number;
  currency?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
};

export function TravelBnplProviderSelection(props: BnplProviderSelectionProps) {
  const { toast } = useToast();
  const [provider, setProvider] = React.useState<BnplProvider>('tabby');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  async function handleCheckout() {
    try {
      setIsSubmitting(true);

      const redirectUrl = await createBnplCheckoutSession({
        provider,
        orderId: props.orderId,
        amount: props.amount,
        currency: props.currency ?? 'AED',
        customer: {
          name: props.customerName,
          email: props.customerEmail,
          phone: props.customerPhone,
        },
      });

      // Redirect user to BNPL provider for KYC and approval
      window.location.href = redirectUrl;
    } catch (err: any) {
      console.error(err);
      toast({
        title: 'Could not start BNPL checkout',
        description:
          err?.message ?? 'Please try again or choose another payment option.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 rounded-2xl border p-4 md:p-6">
      <h3 className="text-lg font-semibold">
        Pay monthly with a BNPL partner
      </h3>
      <p className="text-sm text-muted-foreground">
        Choose a provider to complete your application. You will be redirected
        to a secure Tabby or Tamara page where they handle KYC and approval.
      </p>

      <RadioGroup
        className="grid gap-3 md:grid-cols-2"
        value={provider}
        onValueChange={value => setProvider(value as BnplProvider)}
      >
        <div className="flex items-center gap-3 rounded-xl border px-3 py-2">
          <RadioGroupItem value="tabby" id="bnpl-tabby" />
          <div>
            <Label htmlFor="bnpl-tabby" className="font-medium">
              Tabby
            </Label>
            <p className="text-xs text-muted-foreground">
              Split your trip over flexible monthly payments.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl border px-3 py-2 opacity-80">
          <RadioGroupItem value="tamara" id="bnpl-tamara" />
          <div>
            <Label htmlFor="bnpl-tamara" className="font-medium">
              Tamara
            </Label>
            <p className="text-xs text-muted-foreground">
              Sharia compliant instalment plans for eligible customers.
            </p>
          </div>
        </div>
      </RadioGroup>

      <Button
        onClick={handleCheckout}
        disabled={isSubmitting}
        className="w-full md:w-auto"
      >
        {isSubmitting ? 'Redirecting to provider...' : 'Continue with BNPL'}
      </Button>

      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Flexaura does not issue credit. All financing is provided by licensed
        partners such as Tabby and Tamara. Approval is subject to their own
        checks and terms.
      </p>
    </div>
  );
}
